import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, CheckCircle, Loader2, LogOut } from 'lucide-react';

type TaskType = 'confirmation' | 'intervention';

const PROLIFIC_COMPLETION_URL = 'https://app.prolific.com/submissions/complete?cc=CQBGEYGN';

interface GroupCTask {
  task: TaskType;
  patient_idx: number;
  patient_id: string;
  completed: boolean;
}

interface PatientBrief {
  id: string;
  name: string;
}

const GroupCSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<GroupCTask[]>([]);
  const [progress, setProgress] = useState<{ conf: number; int: number }>({ conf: 0, int: 0 });
  const [allDone, setAllDone] = useState(false);
  const [reviewerMode, setReviewerMode] = useState(false);
  const [allowRevisit, setAllowRevisit] = useState(false);
  const [patientNameById, setPatientNameById] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [taskRes, patientRes] = await Promise.all([
          apiClient.get('/api/auth/group-c-tasks'),
          apiClient.get('/api/patients'),
        ]);

        const redirect = taskRes.data?.redirect;
        if (redirect === 'consent') {
          navigate('/consent');
          return;
        }
        if (redirect === 'survey') {
          navigate('/survey');
          return;
        }

        const loadedTasks: GroupCTask[] = Array.isArray(taskRes.data?.tasks)
          ? taskRes.data.tasks
          : [];
        setTasks(loadedTasks);
        setProgress(taskRes.data?.progress || { conf: 0, int: 0 });
        setAllDone(Boolean(taskRes.data?.all_done));
        setReviewerMode(Boolean(taskRes.data?.reviewer_mode));
        setAllowRevisit(Boolean(taskRes.data?.allow_revisit));

        const patients: PatientBrief[] = Array.isArray(patientRes.data?.patients)
          ? patientRes.data.patients
          : [];
        const nameMap: Record<string, string> = {};
        for (const patient of patients) {
          nameMap[patient.id] = patient.name;
        }
        setPatientNameById(nameMap);
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load sample task selection.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const confirmationTasks = useMemo(
    () => tasks.filter((task) => task.task === 'confirmation').sort((a, b) => a.patient_idx - b.patient_idx),
    [tasks]
  );
  const interventionTasks = useMemo(
    () => tasks.filter((task) => task.task === 'intervention').sort((a, b) => a.patient_idx - b.patient_idx),
    [tasks]
  );

  const startTask = (task: GroupCTask) => {
    navigate(`/patient-chart/${task.patient_id}?task=${task.task}&groupc=1`);
  };

  const handleBack = () => {
    logout();
    navigate('/login');
  };

  const handleCompleteStudy = () => {
    window.location.href = PROLIFIC_COMPLETION_URL;
  };

  const renderTaskCard = (task: GroupCTask) => {
    const name = patientNameById[task.patient_id] || task.patient_id;
    const disableStart = task.completed && !allowRevisit;
    const buttonLabel = task.completed
      ? allowRevisit
        ? 'Open Again'
        : 'Completed'
      : 'Start Task';

    return (
      <Card key={`${task.task}-${task.patient_id}`} className="border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>{name}</span>
            {task.completed ? (
              <Badge className="bg-green-600 hover:bg-green-600">Completed</Badge>
            ) : (
              <Badge variant="outline">Pending</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-xs text-muted-foreground mb-3">
            {task.patient_id} | {task.task}
          </div>
          <Button onClick={() => startTask(task)} disabled={disableStart} className="w-full">
            {buttonLabel}
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">
              {reviewerMode ? 'Sample Cases' : 'Group C Task Selection'}
            </h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-6">
        <Alert className="bg-blue-50 border-blue-200">
          <AlertDescription>
            <strong>{user?.username}</strong> | {reviewerMode ? 'Progress' : 'Group C progress'}: {progress.conf}/5 confirmation, {progress.int}/5 intervention.
            {reviewerMode && (
              <span> You can open any sample case below in any order, including revisiting completed ones.</span>
            )}
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {allDone && !reviewerMode && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-700" />
            <AlertDescription className="text-green-800">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <span>All Group C tasks are completed.</span>
                <Button onClick={handleCompleteStudy} className="bg-green-600 hover:bg-green-700 md:w-auto">
                  Return to Prolific
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Confirmation Tasks</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {confirmationTasks.map(renderTaskCard)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Intervention Tasks</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {interventionTasks.map(renderTaskCard)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GroupCSelectionPage;
